import { ThoughtsAndSpeakUpsResponseInterface } from '@api/thoughtsAndSpeakUpsApiHandler';
import logoLight from '@assets/images/logo-light.svg';
import logo from '@assets/images/logo.svg';
import Img from '@components/Atoms/Img';
import menuConfig from '@config/menu-v2.config';
import { SET_CURRENT_TAB, useGlobalState } from '@context/GlobalStore';
import { SecondaryNavigationContext } from '@context/SecondaryNavigationContext';
import { ThemeContext } from '@context/ThemeContext';
import { getCompanyDetails } from '@helpers/auth.helper';
import { useAppSelector } from '@hooks/useAppSelector';
import classNames from 'classnames';
import { CompanyDetails } from 'custom.d';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, useHistory, useLocation } from 'react-router-dom';
import { RootState } from 'store';
import { ConfigurationState } from 'store/Slices/CompanyConfigSlice';
import Hamburger from './components/Hamburger';
import styles from './index.module.scss';

const NotificationIcon = ({ color, fill }: { color: string; fill: string }) => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18 8C18 6.4087 17.3679 4.88258 16.2426 3.75736C15.1174 2.63214 13.5913 2 12 2C10.4087 2 8.88258 2.63214 7.75736 3.75736C6.63214 4.88258 6 6.4087 6 8C6 15 3 17 3 17H21C21 17 18 15 18 8Z"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={fill}
    />
    <path
      d="M13.73 21C13.5542 21.3031 13.3018 21.5547 12.9982 21.7295C12.6946 21.9044 12.3504 21.9965 12 21.9965C11.6496 21.9965 11.3054 21.9044 11.0018 21.7295C10.6981 21.5547 10.4458 21.3031 10.27 21"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill={fill}
    />
  </svg>
);

export const Header: React.FunctionComponent<HeaderProps> = (props) => {
  const user = localStorage.getItem('user');
  const parsedUser = JSON.parse(user || '{}');
  const { navigationType, latestNotification, handleNotificationOpen, notificationOpen } = props;
  const { isDark } = useContext(ThemeContext);
  const { toggleSubNav } = useContext(SecondaryNavigationContext);
  const history = useHistory();
  const [state, dispatch] = useGlobalState();
  const notifications = false;
  const location = useLocation();
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>();
  const companyConfigData = useAppSelector((st: RootState) => st.companyConfig);
  let organizationLogo;
  let organizationAltText = 'organization logo';

  const getFilteredMenu = (menuItems: any[], companyConfig: ConfigurationState) =>
    menuItems.filter((item) => {
      if (!companyConfig.data.showInstantHelp && item.name === 'Hub') {
        return false;
      }

      if (!companyConfig.data.expertsEnabled && item.name === 'Experts') {
        return false;
      }

      return true;
    });

  const selectedMenuItems = getFilteredMenu(
    (navigationType && menuConfig[navigationType]) || menuConfig.default,
    companyConfigData,
  );

  const defaultSubNav = { id: 1, name: 'Overall' };

  useEffect(() => {
    const getCompanyData = async () => {
      const data = await getCompanyDetails();
      if (data) {
        setCompanyDetails(data);
      }
    };

    getCompanyData();
  }, []);

  if (companyDetails) {
    organizationLogo = companyDetails.company_logo
      ? `${process.env.PUBLIC_URL}/images/logos/${companyDetails.company_logo}`
      : null;
    organizationAltText = companyDetails.company_name && `${companyDetails.company_name} logo`;
  }

  const isEmployerPage = useMemo(
    () => location.pathname.split('/')[1] === 'employer',
    [location.pathname],
  );

  const isSuperAdminPage = useMemo(
    () => location.pathname.split('/')[1] === 'admin',
    [location.pathname],
  );

  const isAdvisorPage = useMemo(
    () => location.pathname.split('/')[1] === 'advisor',
    [location.pathname],
  );

  return (
    <div className={`${styles.hsHeader} ${isDark && styles.hsDark}`}>
      <nav className={styles.hsNav}>
        <div className={styles.hsLogoHolder}>
          {companyConfigData.data.hideHapstarLogo ? (
            <>
              {organizationLogo ? (
                <div
                  className={styles.logo}
                  onClick={() => {
                    toggleSubNav({ id: 1, name: 'Overall' });
                    history.push(
                      isEmployerPage
                        ? '/employer/homepage'
                        : isSuperAdminPage
                        ? '/management/homepage'
                        : isAdvisorPage
                        ? '/advisor/participant-report'
                        : '/',
                    );
                  }}
                >
                  <Img alt={organizationAltText} src={organizationLogo} className={styles.img} />
                </div>
              ) : (
                <div
                  onClick={() => {
                    toggleSubNav({ id: 1, name: 'Overall' });
                    history.push(
                      isEmployerPage
                        ? '/employer/homepage'
                        : isSuperAdminPage
                        ? '/management/homepage'
                        : isAdvisorPage
                        ? '/advisor/participant-report'
                        : '/',
                    );
                  }}
                >
                  <Img
                    alt="Hapstar logo"
                    src={isDark ? logoLight : logo}
                    className={classNames([
                      organizationLogo ? styles.companyLogo : styles.companyFull,
                    ])}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div
                onClick={() => {
                  toggleSubNav({ id: 1, name: 'Overall' });
                  history.push(
                    isEmployerPage
                      ? '/employer/homepage'
                      : isSuperAdminPage
                      ? '/management/homepage'
                      : isAdvisorPage
                      ? '/advisor/participant-report'
                      : '/',
                  );
                }}
              >
                <Img
                  alt="Hapstar logo"
                  src={isDark ? logoLight : logo}
                  className={classNames([
                    organizationLogo ? styles.companyLogo : styles.companyFull,
                  ])}
                />
              </div>
              {organizationLogo && (
                <div>
                  <Img alt={organizationAltText} src={organizationLogo} className={styles.img} />
                </div>
              )}
            </>
          )}
        </div>
        <div className={styles.menuContainer}>
          <ul>
            {selectedMenuItems.map((item, index) => (
              <li key={index}>
                <div
                  onClick={() => {
                    dispatch({ type: SET_CURRENT_TAB, payload: { id: 1, name: 'Overall' } });
                    if (item.name === 'Scores')
                      toggleSubNav(
                        JSON.parse(sessionStorage.getItem('subNav') as any) || defaultSubNav,
                      );
                  }}
                >
                  <NavLink
                    to={item.path}
                    exact={item.exact}
                    activeClassName={styles.active}
                    className={
                      (location.pathname.includes('/post') && item.name === 'Learn') ||
                      (location.pathname.includes('/expert') && item.name === 'Experts') ||
                      (location.pathname.includes('/participant-report') &&
                        item.name === 'Participant Report')
                        ? styles.active
                        : ''
                    }
                  >
                    {item.name}
                  </NavLink>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className={styles.notificationsHamburgerContainer}>
          {/* // TODO: REMOVE V2 CHECK */}
          {location.pathname === '/v2' && (
            <>
              <div className={styles.notificationWrapper} onClick={handleNotificationOpen}>
                {latestNotification ? (
                  <>
                    <div>
                      <NotificationIcon
                        color={notificationOpen ? '#0072b0' : '#505050'}
                        fill={notificationOpen ? '#0072b0' : 'transparent'}
                      />
                    </div>
                    <span className={styles.notificationBadge}>{1}</span>
                  </>
                ) : (
                  <NotificationIcon color="#505050" fill="transparent" />
                )}
              </div>{' '}
            </>
          )}
          <Hamburger isDark={isDark} />
        </div>
      </nav>
    </div>
  );
};

Header.defaultProps = {
  navigationType: 'default',
};

interface HeaderProps {
  navigationType?: 'default' | 'admin' | 'superAdmin' | 'advisor';
  latestNotification: ThoughtsAndSpeakUpsResponseInterface | null;
  handleNotificationOpen: () => void;
  notificationOpen: boolean;
}
